import { router } from '@inertiajs/react'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from '@/components/ui/pagination'
import type { PaginationMeta } from '@/types'

type Props = {
  pagination: PaginationMeta
  baseUrl: string
  filters: Record<string, string | undefined>
}

export default function EventPagination({ pagination, baseUrl, filters }: Props) {
  if (pagination.pages <= 1) return null

  function paramsForPage(page: number) {
    const params = Object.fromEntries(
      Object.entries({ ...filters, page: String(page) }).filter(([, v]) => v && v !== 'all')
    )
    if (page === 1) delete params.page
    return params
  }

  function hrefForPage(page: number) {
    const query = new URLSearchParams(paramsForPage(page)).toString()
    return query ? `${baseUrl}?${query}` : baseUrl
  }

  function navigate(page: number) {
    const params = paramsForPage(page)
    router.get(baseUrl, params, { preserveState: true, preserveScroll: false })
  }

  function getPageNumbers(): (number | 'ellipsis')[] {
    const { page, pages } = pagination
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)

    const items: (number | 'ellipsis')[] = [1]
    if (page > 3) items.push('ellipsis')
    for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) {
      items.push(i)
    }
    if (page < pages - 2) items.push('ellipsis')
    items.push(pages)
    return items
  }

  return (
    <Pagination className="py-8">
      <PaginationContent>
        {pagination.prev && (
          <PaginationItem>
            <PaginationPrevious
              href={hrefForPage(pagination.prev)}
              onClick={(e) => { e.preventDefault(); navigate(pagination.prev!) }}
            />
          </PaginationItem>
        )}
        {getPageNumbers().map((item, i) =>
          item === 'ellipsis' ? (
            <PaginationItem key={`ellipsis-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={item}>
              <PaginationLink
                href={hrefForPage(item)}
                isActive={item === pagination.page}
                onClick={(e) => { e.preventDefault(); navigate(item) }}
              >
                {item}
              </PaginationLink>
            </PaginationItem>
          )
        )}
        {pagination.next && (
          <PaginationItem>
            <PaginationNext
              href={hrefForPage(pagination.next)}
              onClick={(e) => { e.preventDefault(); navigate(pagination.next!) }}
            />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  )
}
